import React from 'react';
import styled from 'styled-components';
import { FaReact, FaNodeJs, FaPython, FaGitAlt, FaDocker } from 'react-icons/fa';
import { SiJavascript, SiTypescript, SiMongodb, SiPostgresql, SiTailwindcss } from 'react-icons/si';
import PText from './PText';
import Button from './Button';
import SectionTitle from './SectionTitle';
import SkillBadge from './SkillBadge';

const AboutSectionStyles = styled.div`
  padding: 10rem 0;
  position: relative;
  
  .section-title {
    text-align: center;
    margin-bottom: 6rem;
  }
  
  .about__content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
    align-items: center;
  }
  
  .about__text {
    .para {
      font-size: 1.7rem;
      line-height: 2;
      color: var(--text-secondary);
      margin-bottom: 2.5rem;
      font-weight: 300;
    }
    
    .highlight {
      color: var(--accent-primary);
      font-weight: 500;
    }
  }
  
  .about__buttons {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
  }
  
  .about__skills {
    background: var(--glass-bg);
    backdrop-filter: blur(10px);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-xl);
    padding: 4rem;
    box-shadow: var(--shadow-lg);
    
    h3 {
      font-size: 2.4rem;
      margin-bottom: 3rem;
      background: var(--gradient-primary);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .skills__grid {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;
    }
  }
  
  .about__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 3rem;
    margin-top: 4rem;
    
    .stat-item {
      text-align: center;
      padding: 2rem;
      background: var(--glass-bg);
      border: 1px solid var(--glass-border);
      border-radius: var(--radius-md);
      transition: var(--transition-normal);
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: var(--shadow-glow);
        background: var(--gradient-primary);
      }
      
      .stat-number {
        font-size: 3.5rem;
        font-weight: 700;
        color: var(--text-primary);
        display: block;
        margin-bottom: 0.5rem;
      }
      
      .stat-label {
        font-size: 1.4rem;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 1px;
      }
    }
  }
  
  @media only screen and (max-width: 968px) {
    .about__content {
      grid-template-columns: 1fr;
      gap: 4rem;
    }
    
    .about__stats {
      grid-template-columns: 1fr;
    }
  }
  
  @media only screen and (max-width: 768px) {
    padding: 8rem 0;
    
    .section-title {
      margin-bottom: 4rem;
    }
    
    .about__skills {
      padding: 3rem 2rem;
      
      h3 {
        font-size: 2rem;
      }
    }
    
    .about__buttons {
      flex-direction: column;
      
      .button-wrapper {
        width: 100%;
        
        .button {
          width: 100%;
          text-align: center;
        }
      }
    }
    
    .about__stats {
      gap: 2rem;
      
      .stat-item {
        .stat-number {
          font-size: 2.8rem;
        }
      }
    }
  }
`;

export default function AboutSection() {
  const skills = [
    { icon: <FaReact />, name: 'React' },
    { icon: <SiJavascript />, name: 'JavaScript' },
    { icon: <SiTypescript />, name: 'TypeScript' },
    { icon: <FaNodeJs />, name: 'Node.js' },
    { icon: <FaPython />, name: 'Python' },
    { icon: <SiMongodb />, name: 'MongoDB' },
    { icon: <SiPostgresql />, name: 'PostgreSQL' },
    { icon: <SiTailwindcss />, name: 'Tailwind CSS' },
    { icon: <FaGitAlt />, name: 'Git' },
    { icon: <FaDocker />, name: 'Docker' },
  ];
  
  return (
    <AboutSectionStyles>
      <div className="container">
        <SectionTitle heading="About Me" subheading="Get to know me" />
        
        <div className="about__content">
          <div className="about__text">
            <PText className="para">
              I'm a <span className="highlight">passionate full-stack developer</span> from eastern North Carolina, 
              specializing in creating beautiful, functional web experiences. With expertise in modern technologies 
              and frameworks, I transform ideas into elegant digital solutions.
            </PText>
            <PText className="para">
              My journey in web development has equipped me with a diverse skill set, from crafting intuitive 
              user interfaces to building robust backend systems. I thrive on challenges and continuously 
              explore new technologies to deliver <span className="highlight">innovative solutions</span>.
            </PText>
            <div className="about__buttons">
              <Button btnLink="/projects" btnText="View Projects" />
              <Button btnLink="/about" btnText="Learn More" outline />
            </div>
          </div>
          
          <div className="about__skills">
            <h3>Tech Stack</h3>
            <div className="skills__grid">
              {skills.map((skill, index) => (
                <SkillBadge key={index} icon={skill.icon} name={skill.name} />
              ))}
            </div>
          </div>
        </div>
        
        <div className="about__stats">
          <div className="stat-item">
            <span className="stat-number">8+</span>
            <span className="stat-label">Years Experience</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">50+</span>
            <span className="stat-label">Projects Completed</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">100%</span>
            <span className="stat-label">Client Satisfaction</span>
          </div>
        </div>
      </div>
    </AboutSectionStyles>
  );
}
